import React, { useState, lazy, Suspense } from 'react';
import { Element } from 'react-scroll';
import { TbBrandGithub, TbExternalLink } from 'react-icons/tb';
import { IoImages } from 'react-icons/io5';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import Experience from './Experience';
import ProjectShot from '../../components/project/ProjectShot';
// Lazy + render-only-when-open: keeps Swiper out of the initial bundle and out
// of the server-side render graph (Swiper touches `window` at import).
const ProjectViewModal = lazy(() => import('./ProjectViewModal'));
import './Works.css';
import { fadeInUp, staggerContainer, cardVariant } from '../../components/functions/animation';
import { projects } from '../../data/projects';

const FILTERS = ['All', 'MERN', 'AI / ML', 'PHP', 'Other'];

function getCategory(tech) {
  if (tech.some(t => ['Python', 'OpenAI API', 'Machine Learning', 'OpenCV', 'ElevenLabs', 'Flask'].includes(t))) return 'AI / ML';
  if (tech.some(t => ['MongoDB', 'Express.js', 'React.js', 'Node.js', 'Next.js'].includes(t))) return 'MERN';
  if (tech.includes('PHP')) return 'PHP';
  return 'Other';
}

const Works = () => {
  const [filter, setFilter] = useState('All');
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const reduceMotion = useReducedMotion();

  const filtered = filter === 'All'
    ? projects
    : projects.filter(p => getCategory(p.tech) === filter);

  const openModal = (project) => { setSelectedProject(project); setIsModalOpen(true); };
  const closeModal = () => { setIsModalOpen(false); setSelectedProject(null); };

  // 3D tilt only when the user hasn't requested reduced motion.
  const cardHover = reduceMotion ? { y: -4 } : { y: -8, rotateX: 3, rotateY: -3, scale: 1.025 };

  return (
    <>
      <Element name="work">
        <section id="work">
          <div className="container">
            {/* Heading */}
            <motion.div variants={fadeInUp} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
              <p className="section-label">What I've Built</p>
              <h2 className="heading">My <span>Projects</span></h2>
            </motion.div>

            {/* Filter tabs */}
            <motion.div
              className="filter-tabs"
              variants={fadeInUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              role="tablist"
              aria-label="Filter projects by category"
            >
              {FILTERS.map(f => (
                <motion.button
                  key={f}
                  role="tab"
                  aria-selected={filter === f}
                  className={`filter-btn${filter === f ? ' active' : ''}`}
                  onClick={() => setFilter(f)}
                  whileHover={{ scale: 1.08, y: -2 }}
                  whileTap={{ scale: 0.93 }}
                  transition={{ type: 'spring', stiffness: 360, damping: 20 }}
                >
                  {f}
                </motion.button>
              ))}
            </motion.div>

            {/* Projects grid */}
            <motion.div
              className="projects-grid"
              variants={staggerContainer}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.05 }}
            >
              <AnimatePresence mode="popLayout">
                {filtered.map((project, index) => {
                  const category = getCategory(project.tech);
                  return (
                    <motion.article
                      className="project-card"
                      key={project.title}
                      layout
                      custom={index}
                      variants={cardVariant}
                      initial="hidden"
                      animate="show"
                      exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
                      style={reduceMotion ? undefined : { transformPerspective: 900 }}
                      whileHover={cardHover}
                      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
                    >
                      {/* Thumbnail — opens media gallery modal */}
                      <button
                        type="button"
                        className="project-thumb"
                        onClick={() => openModal(project)}
                        aria-label={`View ${project.title} media gallery`}
                      >
                        <ProjectShot
                          src={project.image}
                          srcSet={project.imageSm ? `${project.imageSm} 400w, ${project.image} 800w` : undefined}
                          avif={project.imageAvif}
                          avifSm={project.imageAvifSm}
                          alt={project.title}
                          width={800}
                          height={500}
                        />
                        <span className="project-thumb-hint" aria-hidden="true">
                          <IoImages /> View Gallery
                        </span>
                      </button>

                      {/* Persistent content — always visible on every viewport.
                          Clicking the body opens the project (mouse convenience;
                          keyboard users use the thumbnail button above). */}
                      <div className="project-body" onClick={() => openModal(project)}>
                        <p className="proj-category-badge">{category}</p>
                        <h3 className="proj-title">{project.title}</h3>
                        <p className="proj-desc">{project.description}</p>

                        <div className="proj-tech">
                          {project.tech.slice(0, 4).map((t, i) => (
                            <span key={i} className="tech-chip">{t}</span>
                          ))}
                          {project.tech.length > 4 && (
                            <span className="tech-chip">+{project.tech.length - 4}</span>
                          )}
                        </div>

                        <div className="proj-actions" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="proj-action-btn"
                            onClick={() => openModal(project)}
                            aria-label={`Open ${project.title} media gallery`}
                          >
                            <IoImages aria-hidden="true" />
                          </button>
                          {project.liveLink ? (
                            <a href={project.liveLink} target="_blank" rel="noopener noreferrer" className="proj-action-btn" aria-label={`View ${project.title} live`}>
                              <TbExternalLink aria-hidden="true" />
                            </a>
                          ) : (
                            <button type="button" className="proj-action-btn disabled" disabled aria-disabled="true" aria-label="Live link unavailable"><TbExternalLink aria-hidden="true" /></button>
                          )}
                          {project.githubLink ? (
                            <a href={project.githubLink} target="_blank" rel="noopener noreferrer" className="proj-action-btn" aria-label={`${project.title} on GitHub`}>
                              <TbBrandGithub aria-hidden="true" />
                            </a>
                          ) : (
                            <button type="button" className="proj-action-btn disabled" disabled aria-disabled="true" aria-label="Source is private"><TbBrandGithub aria-hidden="true" /></button>
                          )}
                        </div>
                      </div>
                    </motion.article>
                  );
                })}
              </AnimatePresence>
            </motion.div>
          </div>

          <Experience />
        </section>
      </Element>

      {isModalOpen && (
        <Suspense fallback={null}>
          <ProjectViewModal
            isOpen={isModalOpen}
            onClose={closeModal}
            project={selectedProject}
          />
        </Suspense>
      )}
    </>
  );
};

export default Works;
